import React, { useState, useEffect, useRef, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import PriceCalculator from "@heartinz/gst_india_product_price_calculator";
import { formatCurrencyFun ,formatCurrencyFunWithoutConvert,getDisplayPrices} from "../../../utils/formatCurrencyFunc";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";

import MatchStock from "./MatchStock";
import ViewStockTransfer from "./ViewStockTransfer";

import { useAuth } from "../../../context/AuthProvider";
import { toPrimaryQuantity, toSecondaryUnitPrice } from "@heartinz/finance";
import ProductSearchList from "../../../constants/ProductSearchList";
import { ChevronRightIcon, MailSendIcon, SearchIcon, StoreAltIcon, TrashIcon, UserIcon, XIcon } from "../../../constants/Icons";

const StockTransfer = ({ isActive }) => {
  const { isUser } = useAuth();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const suggestionRefs = useRef([]);
  const suggestionStoreRefs = useRef([]);
  const addBtnRef = useRef([]);
  const [searchProductTerm, setSearchProductTerm] = useState("");
  const searchProductRef = useRef(null);
  const [suggestionProduct, setProductSuggestions] = useState([]);
  const [activeSuggestionType, setActiveSuggestionType] = useState("product"); // or "store"
  const [activeIndex, setActiveIndex] = useState(-1);

  const [isStockModalOpen, setStockModalOpen] = useState(false);
  const [stockData, setStockData] = useState(null);
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const [stockCart, setStockCart] = useState(() => {
    const savedCart = localStorage.getItem("nGbLtcart");
    return savedCart && savedCart !== "undefined" ? JSON.parse(savedCart) : [];
  });
  // console.log("Initial cart:", stockCart);
  const [totalPayable, setTotalPayable] = useState(0);
  const [totalMRP, setTotalMRP] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalSaleprice, setTotalSalePrice] = useState(0);
  const [totalTaxamount, setTotalTaxAmount] = useState(0);
  const [totalBaseprice, setTotalBasePrice] = useState(0);
  const [totalQty, setTotalQuantity] = useState(0);
  const [totalRes, setTotals] = useState({});

  const [transfercharge, setTransferCharge] = useState(0);

  const [searchStoreTerm, setSearchStoreTerm] = useState("");
  const searchStoreRef = useRef(null);
  const [suggestionStore, setStoreSuggestions] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);

  const [isBtnLoading, setBtnLoading] = useState(false);

  const [isStockTransferModalOpen, setStockTransferModalOpen] = useState(false);
  const [isData, setStockTransferData] = useState(null);

  // Handle clicks outside search inputs
  const handleOutsideClick = (e) => {
    if (
      searchProductRef.current &&
      !searchProductRef.current.contains(e.target)
    ) {
      setProductSuggestions([]);
      setSearchProductTerm("");
    }
    if (searchStoreRef.current && !searchStoreRef.current.contains(e.target)) {
      setStoreSuggestions([]);
      setSearchStoreTerm("");
    }
  };

  // Product search
  const handleProductSearch = (term) => {
    setSearchProductTerm(term);
    if (term.trim() === "") {
      setProductSuggestions([]);
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        controllerRef.current = new AbortController();
        const currentId = ++requestIdRef.current;

        const response = await AxiosInstance.get(
          `module/product/search-hybrid-active?searchtxt=${term}`,
          { signal: controllerRef.current.signal },
        );
        if (currentId === requestIdRef.current) {
          setProductSuggestions(response.data.results || []);
        }
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          console.error("API Error:", error);
        }
      }
    }, 200);
  };

  // Handle product selection
  const handleSelectedProduct = async (product) => {
    try {
      if (!checkNetwork()) return;

      const response = await AxiosInstance.get(
        `module/stock/batch/${product._id}`,
      );

      const result = response.data?.results;

      if (result?.stock?.length > 0) {
        const productData = result.product || {};

        const formattedData = result.stock.map((item) => ({
          ...item,

          // merge product details
          title: productData.title,
          code: productData.code,
          hsnsac: productData.hsnsac,
          productLandingCost: productData.landingcost,
          productUnitConfig: productData.unitConfig,

          // keep stock landing cost
          landingcost: CurrencyConverter.toCurrency(
            item.landingcost || 0,
            "INR",
          ),

          stockLeftUnit:
            item?.unitConfig?.primaryUnitCode ||
            productData?.unitConfig?.primaryUnitCode ||
            "",
        }));

        setStockData(formattedData);
        setStockModalOpen(true);
      } else {
        showAlert("warning", Messages.ERROR.NO_STOCK);
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setSearchProductTerm("");
      setProductSuggestions([]);
    }
  };

  // Store search
  const handleStoreSearch = async (term) => {
    setSearchStoreTerm(term);
    if (term.trim() === "") {
      setStoreSuggestions([]);
      return;
    }
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.post(`search-store`, {
        searchtxt: term,
      });
      setStoreSuggestions(response.data.results || []);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleSelectedStore = (store) => {
    setSelectedStore(store);
    setSearchStoreTerm("");
    setStoreSuggestions([]);
    setActiveIndex(-1);

    setTimeout(() => {
      addBtnRef.current?.focus();
    }, 0);
  };

  const addToStockCart = useCallback((product) => {
    // console.log("Adding to cart:", product);
    const quantity = product.quantity;
    setStockCart((prevCart) => {
      const existingProduct = prevCart.find(
        (item) =>
          item.productid === product.productid &&
          item.stocklid === product.stocklid &&
          item.stockid === product.stockid,
      );
      if (existingProduct) {
        const updatedProduct = PriceCalculator.getAllPriceList(
          product.mrp,
          product.saleprice,
          product.baseprice,
          product.taxpercentage,
          quantity,
          product.isLock,
        );
        return prevCart.map((item) =>
          item.productid === product.productid &&
          item.stocklid === product.stocklid &&
          item.stockid === product.stockid
            ? {
                ...item,
                quantity: updatedProduct.quantity,
                lineGrandTotal: updatedProduct.lineGrandTotal,
                lineTotalBasePrice: updatedProduct.lineTotalBasePrice,
                lineTotalDiscount: updatedProduct.lineTotalDiscount,
                lineTotalMrp: updatedProduct.lineTotalMrp,
                lineTotalSalePrice: updatedProduct.lineTotalSalePrice,
                lineTotalTaxAmount: updatedProduct.lineTotalTaxAmount,
                landingcost: product.landingcost ?? 0,
              }
            : item,
        );
      } else {
        const productItem = PriceCalculator.getAllPriceList(
          product.mrp,
          product.saleprice,
          product.baseprice,
          product.taxpercentage,
          quantity,
          product.isLock,
        );
        return [
          ...prevCart,
          {
            ...product,
            quantity: quantity,
            lineGrandTotal: productItem.lineGrandTotal,
            lineTotalBasePrice: productItem.lineTotalBasePrice,
            lineTotalDiscount: productItem.lineTotalDiscount,
            lineTotalMrp: productItem.lineTotalMrp,
            lineTotalSalePrice: productItem.lineTotalSalePrice,
            lineTotalTaxAmount: productItem.lineTotalTaxAmount,
            landingcost: product.landingcost ?? 0,
          },
        ];
      }
    });
    setStockModalOpen(false);
    searchProductRef.current.focus();
  }, []);

  const removeItem = (indexToRemove) => {
    setStockCart((prevCart) =>
      prevCart.filter((_, index) => index !== indexToRemove),
    );
  };

  const calculateTotal = () => {
    const totalMrPrice = stockCart.reduce(
      (total, item) => total + item.lineTotalMrp,
      0,
    );
    setTotalMRP(CurrencyConverter.toCurrency(totalMrPrice || 0, "INR"));

    const totalSale = stockCart.reduce(
      (total, item) => total + item.lineTotalSalePrice,
      0,
    );
    setTotalSalePrice(CurrencyConverter.toCurrency(totalSale || 0, "INR"));

    const totalBase = stockCart.reduce(
      (total, item) => total + item.lineTotalBasePrice,
      0,
    );
    setTotalBasePrice(CurrencyConverter.toCurrency(totalBase || 0, "INR"));

    const totalTax = stockCart.reduce(
      (total, item) => total + item.lineTotalTaxAmount,
      0,
    );
    setTotalTaxAmount(CurrencyConverter.toCurrency(totalTax || 0, "INR"));

    const totalQty = stockCart.reduce(
      (total, item) => total + item.quantity,
      0,
    );
    setTotalQuantity(totalQty);

    const totalRes = PriceCalculator.getGrandTotal(
      totalMrPrice,
      totalSale,
      totalBase,
      totalTax,
    );
    setTotals(totalRes);

    setTotalPayable(totalRes.grandtotal);
  };

  const handleAdd = async () => {
    const savedCart = localStorage.getItem("nGbLtcart");
    const parsedCart = savedCart ? JSON.parse(savedCart) : [];

    if (selectedStore) {
      const stockData = {
        transfertoid: selectedStore._id,

        transferitems: parsedCart.map((item) => {
          const hasUnitConfig =
            item?.unitConfig && item?.unitConfig?.primaryUnitCode;

          return {
            stocklinkid: item.stockLinkId,

            // already converted in MatchStock
            quantity: Number(item.quantity),

            landingcost: item.landingcost || 0,

            // send only when unitConfig exists
            ...(hasUnitConfig && {
              isSecondaryUnit: item.isSecondaryUnit,
              unitConfig: item.unitConfig,
            }),
          };
        }),
      };

      // console.log("Prepared stock data for transfer:", stockData);

      setBtnLoading(true);

      try {
        if (!checkNetwork()) return;

        const stockResp = await AxiosInstance.post(
          `module/stocktransfer`,
          stockData,
        );

        showAlert("success", Messages.SUCCESS.STOCK_TRANSFER);

        setStockModalOpen(false);
        setStockTransferModalOpen(true);
        setStockTransferData(stockResp.data.results);

        handleReset();
      } catch (error) {
        handleError(error, showAlert);
        searchProductRef.current?.focus();
        return false;
      } finally {
        setBtnLoading(false);
      }
    } else {
      showAlert("warning", Messages.ERROR.EMPTY_STORE);
      searchStoreRef.current.focus();
    }
  };

  const handleReset = () => {
    setStockCart([]);
    localStorage.removeItem("nGbLtcart");
    setProductSuggestions([]);
    setSearchProductTerm("");
    setSelectedStore(null);
    setSearchStoreTerm("");
    setStoreSuggestions([]);
    setTransferCharge(0);
    setStockData(null);
    searchProductRef.current?.focus();
  };

  const handleClose = () => {
    setStockModalOpen(false);
    setStockData(null);
  };

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "main":
        return "bg-label-orange";
      case "sub":
        return "bg-label-peach";
      case "hub":
        return "bg-label-night";
      default:
        return "bg-label-secondary";
    }
  };

  
  const handleTransferClose = () => {
    setStockTransferModalOpen(false);
    setStockTransferData(null);
    searchProductRef.current?.focus();
  };

  const handleStockUpdate = (updatedData) => {};

  const handleInputChange = (setValue) => (e) => {
    const value = e.target.value;
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      setValue(value);
    } else {
      setValue(0);
    }
  };

  const handleSuggestionNavigation = (e) => {
    const isProduct = activeSuggestionType === "product";
    const suggestions = isProduct ? suggestionProduct : suggestionStore;
    const handleSelect = isProduct
      ? handleSelectedProduct
      : handleSelectedStore;

    if (e.key === "Enter") {
      e.preventDefault();

      if (isProduct && searchProductTerm.trim() === "") {
        searchStoreRef.current?.focus();
        return;
      }

      if (activeIndex >= 0 && suggestions[activeIndex]) {
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    }
  };

  const handleEnterFocus = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef?.current?.focus();
    }
  };

  useEffect(() => {
    const currentRefs =
      activeSuggestionType === "product"
        ? suggestionRefs.current
        : suggestionStoreRefs.current;

    const suggestions =
      activeSuggestionType === "product" ? suggestionProduct : suggestionStore;
    if (
      activeIndex >= 0 &&
      suggestions.length > 0 &&
      currentRefs.length === suggestions.length &&
      currentRefs[activeIndex]
    ) {
      currentRefs[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex, activeSuggestionType, suggestionProduct, suggestionStore]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      searchProductRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  useEffect(() => {
    localStorage.setItem("nGbLtcart", JSON.stringify(stockCart));
    calculateTotal();
  }, [stockCart]);

  useEffect(() => {}, [addToStockCart]);

  const displayPriceTotalMrp = stockCart.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
  
    // const mrp = Number(displayPrices.mrp || 0);
  
    // const qty = item?.isSecondaryUnit
    //   ? Number(item.enteredquantity || 0)
    //   : Number(item.quantity || 0);
  
    return total + displayPrices.lineTotalMrp;
  }, 0);

  const displayPriceTotalDisc = stockCart.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
  
    return total + displayPrices.lineTotalDisc;
  }, 0);
  
  const displayPriceTotalSalePrice = stockCart.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
  
    return total + displayPrices.lineTotalSalePrice;
  }, 0);
  return (
    <>
      <div className="page-wrapper">
        <div className="page-content">
          <div className="row py-0">
            <div className="col-lg-9">
              <div className="card">
                <div className="card-header px-3 py-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <h6 className="mb-0">Stock Transfer</h6>
                </div>
                <div className="card-body mt-3 py-0">
                  <div className="row g-2">
                    <div className="col-sm-8" style={{ position: "relative" }}>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          name="search"
                          autoComplete="off"
                          value={searchProductTerm}
                          ref={searchProductRef}
                          onFocus={() => setActiveSuggestionType("product")}
                          onChange={(e) => {
                            handleProductSearch(e.target.value);
                            setActiveIndex(-1);
                          }}
                          onKeyDown={handleSuggestionNavigation}
                          placeholder="Search product by title, code..."
                        />
                        <button className="input-group-text">
                          <SearchIcon/>
                        </button>
                      </div>
                      {searchProductTerm && suggestionProduct.length === 0 && (
                        <ul className="suggestions-list">
                          <li className="suggestion-item text-muted">
                            No products data was found
                          </li>
                        </ul>
                      )}
                      {suggestionProduct.length > 0 && (
                        <ProductSearchList
                          suggest={suggestionProduct}
                          suggestionRefs={suggestionRefs}
                          handleSelect={handleSelectedProduct}
                          activeIndex={activeIndex}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-body px-0">
                  <div className="table-responsive mt-0">
                  <div className="stock-table-scroll">
                    <table className="table table-bordered align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="text-center">S.No</th>
                          <th className="text-center">Description</th>
                          <th className="text-center">MRP</th>
                          <th className="text-center">Discount</th>
                          <th className="text-center">Sale Price</th>
                          <th className="text-center">Qty</th>
                          <th className="text-center">Total</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockCart.map((item, index) => {
                          const displayPrices = getDisplayPrices(item);
                          const displayUnit = item?.unitConfig
                            ? item?.isSecondaryUnit
                              ? item?.unitConfig?.secondaryUnitCode
                              : item?.unitConfig?.primaryUnitCode
                            : "Unit";
                        return(
                          <tr
                            key={item._id + index}
                            onClick={() => removeItem(index)}
                            className="position-relative  cursor-poInter hover-row"
                          >
                            <td className="text-center">{index + 1}</td>
                            <td className="text-wrap text-left">
                              {item.title} -{" "}
                              <small className="badge rounded-pill bg-label-peace fs-10">
                                {item.taxpercentage}%
                              </small>
                            </td>
                            <td className="text-end">
                              {formatCurrencyFunWithoutConvert(displayPrices.mrp)}
                            </td>
                            <td className="text-end">
                                {formatCurrencyFunWithoutConvert(displayPrices.discount)}
                            </td>
                            <td className="text-end">
                              
                                {formatCurrencyFunWithoutConvert(displayPrices.saleprice)}
                            </td>
                            <td width={120} className="text-center">
                              {item?.isSecondaryUnit
                                ? Number(item.enteredquantity)
                                : Number(item.quantity)}{" "}
                                {displayUnit}
                            </td>
                            <td className="text-end">
                              {formatCurrencyFun(item.lineGrandTotal)}
                            </td>
                            <td className="text-center">
                              <div className="order-actions opacity-0 opacity-md-100">
                                <button
                                  className="btn-label-danger shadow-none"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeItem(index);
                                  }}
                                >
                                  <TrashIcon/>
                                </button>
                              </div>
                            </td>
                          </tr>
                        )})}
                        {/* <tr>
                          <td colSpan={2} className="text-center fw-medium">
                            Total
                          </td>
                          <td className="text-end fw-medium">
                             {formatCurrencyFun(totalRes.totalmrp)}
                          </td>
                          <td className="text-end fw-medium">
                           
                            {formatCurrencyFun(totalRes.totaldiscount)}
                          </td>
                          <td className="text-end fw-medium">
                             {formatCurrencyFun(totalRes.totalsaleprice)}
                          </td>
                          <td className="text-center fw-medium"></td>
                          <td className="text-end fw-medium">
                            {formatCurrencyFun(totalRes.grandtotal)}
                          </td>
                          <td></td>
                        </tr> */}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="ms-auto">
                  <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total Mrp :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                        
                        {formatCurrencyFunWithoutConvert(displayPriceTotalMrp)}
                      </h6>
                    </div>
                  <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total Discount :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                        
                        {formatCurrencyFunWithoutConvert(displayPriceTotalDisc)}
                      </h6>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total salePrice :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                       
                        {formatCurrencyFunWithoutConvert(displayPriceTotalSalePrice)}
                      </h6>
                    </div>
                    <div className="bg-light rounded p-2 text-center">
                      <h6 className="mb-0 fs-18 text-dark">
                        Grand Total :{" "}
                        <span>
                          {formatCurrencyFun(totalRes.grandtotal)}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body">
                  <div className="ms-auto position-relative">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        name="search"
                        ref={searchStoreRef}
                        autoComplete="off"
                        value={searchStoreTerm}
                        onFocus={() => setActiveSuggestionType("store")}
                        onChange={(e) => {
                          handleStoreSearch(e.target.value);
                          setActiveIndex(-1);
                        }}
                        onKeyDown={handleSuggestionNavigation}
                        placeholder="Search store by title, email..."
                      />
                      <button className="input-group-text">
                        <SearchIcon/>
                      </button>
                    </div>
                    {searchStoreTerm && suggestionStore.length === 0 && (
                      <ul className="suggestions-list">
                        <li className="suggestion-item text-muted">
                          No store data was found
                        </li>
                      </ul>
                    )}
                    {suggestionStore.length > 0 && (
                      <ul className="suggestions-list">
                        {suggestionStore.map(
                          (store, index) =>
                            store._id !== isUser.storeId && (
                              <li
                                key={store._id}
                                ref={(el) => {
                                  suggestionStoreRefs.current[index] = el;
                                }}
                                onClick={() => handleSelectedStore(store)}
                                className={`suggestion-item ${
                                  index === activeIndex
                                    ? "active-suggestion"
                                    : ""
                                }`}
                              >
                                <span
                                  className={`badge rounded-pill ${getColor(
                                    store.stype,
                                  )}`}
                                >
                                  {store.stype}
                                </span>
                                <small> - </small>
                                {store.title} -{" "}
                                <span className="badge badge-primary badge bg-label-sea rounded-pill">
                                  {store.email}
                                </span>
                              </li>
                            ),
                        )}
                      </ul>
                    )}
                  </div>
                  <div className="col-lg-12 mt-2">
                    <div className="card shadow-none border-blue mb-0">
                      <div className="card-header bg-light-blue p-2">
                        <div className="mb-0">Store info</div>
                      </div>
                      <div className="card-body p-2">
                        {selectedStore ? (
                          <>
                          <p className="mb-0 d-flex align-items-center gap-2">
                          <StoreAltIcon iClass={"fs-6"}/>{" "}
                              <span className="fw-600">
                                <span
                                  className={`badge rounded-pill ${getColor(
                                    selectedStore.stype,
                                  )}`}
                                >
                                  {selectedStore.stype}
                                </span>{" "}
                              </span>
                            </p>
                            <p className="mb-0 d-flex align-items-center gap-2">
                            <UserIcon iClass={"fs-6"}/>{" "}
                              <span className="fw-600">
                                <span
                                  className={`badge rounded-pill ${getColor(
                                    selectedStore.stype,
                                  )}`}
                                >
                                </span>{" "}
                                {selectedStore.title}
                              </span>
                            </p>
                            <p className="mb-0 d-flex align-items-center gap-2">
                              <MailSendIcon iClass={"fs-6"}/>
                              <span className="fw-600">{selectedStore.email}</span>
                            </p>
                          </>
                        ) : (
                          <p className="mb-1 text-muted">
                            No store has been selected
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="d-block mt-4">
                    <button
                      type="button"
                      className="btn shadow-none btn-outline-secondary text-left"
                      onClick={handleReset}
                    >
                      <XIcon/> Cancel
                    </button>
                    {isBtnLoading ? (
                      <button className="btn btn-teal pb-0 text-right" disabled>
                        <CircularProgress size={22} className="text-white" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-teal text-right w-48"
                        onClick={handleAdd}
                        ref={addBtnRef}
                      >
                        Transfer <ChevronRightIcon/>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isStockModalOpen && (
          <MatchStock
            data={stockData}
            addToStockCart={addToStockCart}
            onClose={handleClose}
          />
        )}
        {isStockTransferModalOpen && (
          <ViewStockTransfer
            data={isData}
            onClose={handleTransferClose}
            onStockUpdate={handleStockUpdate}
          />
        )}
      </div>
    </>
  );
};

export default StockTransfer;
